"use client";

import { CheckIcon, ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { useRef } from "react";

import CoachAvatar from "#/components/shared/coach-avatar";
import { Button } from "#/components/ui/button";
import { formatPrice } from "#/lib/format";

import type { Coach, Service } from "./booking-types";

// ─── Staggered Animation ───

export function StaggeredItem({ index, children }: { index: number; children: React.ReactNode }) {
  return (
    <div
      className="animate-in fade-in slide-in-from-bottom-2 fill-mode-both"
      style={{ animationDelay: `${index * 40}ms`, animationDuration: "300ms" }}
    >
      {children}
    </div>
  );
}

// ─── Step Indicator ───

export function StepIndicator({ current, accentColor }: { current: number; accentColor: string }) {
  const steps = ["Book", "Details", "Confirm"];
  return (
    <div className="flex items-center justify-center gap-0 py-3 sm:py-4 mb-0.5">
      {steps.map((label, i) => {
        const isActive = i === current;
        const isComplete = i < current;
        return (
          <div key={label} className="flex items-center">
            {i > 0 && (
              <div
                className="w-6 sm:w-10 lg:w-16 h-[2px] mx-1 sm:mx-1.5 rounded-full transition-all duration-500"
                style={{ backgroundColor: isComplete ? accentColor : "var(--bf-border)" }}
              />
            )}
            <div className="flex flex-col items-center gap-0.5">
              <div
                className="flex items-center justify-center rounded-full transition-all duration-500 text-2xs sm:text-xs font-bold shrink-0"
                style={
                  isActive
                    ? {
                        width: 24,
                        height: 24,
                        backgroundColor: accentColor,
                        color: "white",
                        boxShadow: `0 0 0 3px ${accentColor}20, 0 2px 8px ${accentColor}30`,
                      }
                    : isComplete
                      ? { width: 24, height: 24, backgroundColor: accentColor, color: "white" }
                      : {
                          width: 24,
                          height: 24,
                          backgroundColor: "var(--bf-bg-elevated)",
                          color: "var(--bf-text-muted)",
                          border: "1.5px solid var(--bf-border)",
                        }
                }
              >
                {isComplete ? <CheckIcon className="h-3 w-3" strokeWidth={2.5} /> : i + 1}
              </div>
              <span
                className="text-2xs sm:text-sm font-semibold tracking-[-0.01em] transition-colors duration-300 whitespace-nowrap"
                style={{
                  color: isActive
                    ? accentColor
                    : isComplete
                      ? "var(--bf-text)"
                      : "var(--bf-text-muted)",
                }}
              >
                {label}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Section Header ───

export function BookingSectionHeader({
  icon: Icon,
  title,
  subtitle,
}: {
  icon: React.ElementType;
  title: string;
  subtitle?: string;
}) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <div
        className="h-9 w-9 rounded-lg flex items-center justify-center"
        style={{ backgroundColor: "var(--bf-bg-elevated)" }}
      >
        <Icon
          className="h-[17px] w-[17px]"
          strokeWidth={1.8}
          style={{ color: "var(--bf-text-secondary)" }}
        />
      </div>
      <div>
        <h2
          className="font-semibold text-md tracking-[-0.02em]"
          style={{ color: "var(--bf-text)" }}
        >
          {title}
        </h2>
        {subtitle && (
          <p className="text-md mt-0.5" style={{ color: "var(--bf-text-secondary)" }}>
            {subtitle}
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Section Divider ───

export function SectionDivider() {
  return (
    <div className="py-1">
      <div className="h-px" style={{ backgroundColor: "var(--bf-border)" }} />
    </div>
  );
}

// ─── Calendar ───

export function MonthCalendar({
  availableDates,
  selectedDate,
  onSelectDate,
  currentMonth,
  onMonthChange,
  loadingDates,
  accentColor,
}: {
  availableDates: Set<string>;
  selectedDate: string;
  onSelectDate: (d: string) => void;
  currentMonth: Date;
  onMonthChange: (d: -1 | 1) => void;
  loadingDates: boolean;
  accentColor: string;
}) {
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const hasMoved = useRef(false);
  const calRef = useRef<HTMLDivElement>(null);

  function handleTouchStart(e: React.TouchEvent) {
    touchStartX.current = e.touches[0].clientX;
    touchEndX.current = e.touches[0].clientX;
    hasMoved.current = false;
  }
  function handleTouchMove(e: React.TouchEvent) {
    touchEndX.current = e.touches[0].clientX;
    hasMoved.current = true;
  }
  function handleTouchEnd() {
    if (!hasMoved.current) return;
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50) {
      onMonthChange(diff > 0 ? 1 : -1);
    }
  }

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  const MONTHS = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const monthName = `${MONTHS[month]} ${year}`;
  const cells: (number | null)[] = [
    ...Array(firstDay).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];
  const ds = (day: number) =>
    `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

  return (
    <div
      className="space-y-3"
      ref={calRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div className="flex items-center justify-between px-1">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onMonthChange(-1)}
          className="rounded-lg h-8 w-8 transition-colors"
          style={{ backgroundColor: "var(--bf-bg-elevated)" }}
          aria-label="Previous month"
        >
          <ChevronLeftIcon
            className="h-4 w-4"
            strokeWidth={2}
            style={{ color: "var(--bf-text-secondary)" }}
          />
        </Button>
        <span
          className="font-semibold text-md tracking-[-0.01em]"
          style={{ color: "var(--bf-text)" }}
        >
          {monthName}
        </span>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onMonthChange(1)}
          className="rounded-lg h-8 w-8 transition-colors"
          style={{ backgroundColor: "var(--bf-bg-elevated)" }}
          aria-label="Next month"
        >
          <ChevronRightIcon
            className="h-4 w-4"
            strokeWidth={2}
            style={{ color: "var(--bf-text-secondary)" }}
          />
        </Button>
      </div>
      <div className="grid grid-cols-7 gap-0.5 px-0.5">
        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
          <div
            key={`${d}-${i}`}
            className="h-8 flex items-center justify-center text-xs font-medium"
            style={{ color: "var(--bf-text-muted)" }}
          >
            {d}
          </div>
        ))}
        {cells.map((day, i) => {
          if (!day) return <div key={`e-${i}`} />;
          const d = ds(day);
          const date = new Date(year, month, day);
          const isPast = date < today;
          const hasSlots = availableDates.has(d);
          const isSelected = d === selectedDate;
          const isToday = d === todayStr;
          return (
            <button
              key={d}
              disabled={isPast || (!loadingDates && !hasSlots)}
              onClick={() => !isPast && hasSlots && onSelectDate(d)}
              className={[
                "relative h-10 w-full rounded-lg text-md transition-all duration-200 flex flex-col items-center justify-center",
                isSelected
                  ? "text-white font-semibold"
                  : hasSlots && !isPast
                    ? "font-normal cursor-pointer hover:brightness-95"
                    : "",
              ].join(" ")}
              style={
                isSelected
                  ? { backgroundColor: accentColor, boxShadow: `0 2px 8px ${accentColor}30` }
                  : hasSlots && !isPast
                    ? { backgroundColor: "var(--bf-bg)", color: "var(--bf-text)" }
                    : { color: "var(--bf-text-disabled)" }
              }
            >
              {day}
              {isToday && !isSelected && (
                <span
                  className="absolute bottom-[3px] h-[3px] w-[3px] rounded-full"
                  style={{ backgroundColor: "var(--bf-text-muted)" }}
                />
              )}
              {hasSlots && !isSelected && !isPast && !isToday && (
                <span
                  className="absolute bottom-[3px] h-[3px] w-[3px] rounded-full"
                  style={{ backgroundColor: accentColor, opacity: 0.45 }}
                />
              )}
              {loadingDates && !isPast && !isSelected && (
                <span
                  className="absolute inset-0.5 rounded-md animate-pulse"
                  style={{ backgroundColor: "var(--bf-bg-elevated)" }}
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Coach Profile Card ───
//
// Compact card used inside the /book wizard's coach chooser. Stays the same
// height whether or not it's selected — selecting only flips the visual
// "selected" treatment (border + tint + checkmark). Bio is single-line
// clamped; services live in the dedicated "Select a Service" section
// above/below and never duplicate inside the card. Detailed coach profile
// content (full bio, all services, social links) lives on the public
// storefront's CoachDetailSheet — this booking-wizard card is intentionally
// minimal so the layout stays scannable on iPhone.

export function CoachProfileCard({
  coach,
  services,
  accentColor,
  isSelected,
  onSelect,
  onAbout,
  hasSheetContent,
  index,
}: {
  coach: Coach;
  services: Service[];
  accentColor: string;
  isSelected: boolean;
  onSelect: () => void;
  // Optional: when provided AND the coach has meaningful profile content
  // (gated by hasSheetContent), the card renders a "View profile" footer
  // button that opens the detail sheet without changing selection.
  onAbout?: () => void;
  hasSheetContent?: boolean;
  index: number;
}) {
  const coachServices = services.filter((s) => coach.serviceIds.includes(s.id));
  const getCoachPrice = (s: Service) => coach.servicePrices?.[s.id] ?? s.priceInCents;
  const priceRange =
    coachServices.length > 0
      ? coachServices.reduce(
          (acc, s) => {
            const p = getCoachPrice(s);
            return { min: Math.min(acc.min, p), max: Math.max(acc.max, p) };
          },
          { min: Infinity, max: 0 },
        )
      : null;

  const showAbout = !!onAbout && hasSheetContent;

  return (
    <StaggeredItem index={index + 1}>
      <div
        className="group relative w-full h-full flex flex-col rounded-2xl transition-all duration-200 overflow-hidden hover:-translate-y-0.5"
        style={
          isSelected
            ? {
                backgroundColor: `${accentColor}04`,
                border: `1.5px solid ${accentColor}`,
                boxShadow: `0 0 0 3px ${accentColor}08`,
              }
            : {
                backgroundColor: "var(--bf-bg-card)",
                border: "1px solid var(--bf-border)",
                boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
              }
        }
      >
        {/* Hover affordance — accent border fades in when not selected,
            matching the service cards so the tile reads as obviously
            selectable. Separate element so it doesn't fight the inline
            base/selected border. */}
        {!isSelected && (
          <div
            className="pointer-events-none absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-200"
            style={{ border: `1.5px solid ${accentColor}66` }}
          />
        )}

        {/* Card body — tapping this area selects the coach. flex-1 so the
            "View profile" footer sits at the bottom of the tile. */}
        <button
          type="button"
          aria-pressed={isSelected}
          aria-label={
            isSelected
              ? `${coach.name || "Instructor"} selected`
              : `Select ${coach.name || "Instructor"}`
          }
          onClick={onSelect}
          className="block w-full flex-1 text-left p-4"
        >
          <div className="flex items-start gap-3">
            <div className="relative shrink-0">
              <CoachAvatar
                name={coach.name}
                image={coach.image}
                size={52}
                accentColor={accentColor}
              />
            </div>
            <div className="flex-1 min-w-0">
              <p
                className="font-semibold text-md tracking-[-0.01em] truncate"
                style={{ color: "var(--bf-text)" }}
              >
                {coach.name || "Instructor"}
              </p>
              {priceRange && priceRange.min < Infinity && (
                <p className="text-xs mt-0.5" style={{ color: "var(--bf-text-muted)" }}>
                  from {formatPrice(priceRange.min)}
                </p>
              )}
            </div>
          </div>

          {/* Bio intentionally NOT shown on the card: it created a
              contradiction with the profile drawer (card read `bio`, drawer
              prefers `extendedBio`). The card is a clean selection card —
              avatar / name / price / Select / View profile. Full bio lives
              in the drawer (CoachDetailSheet), unchanged. */}

          {/* Primary selection affordance — same Select / Selected pill as
              the service cards so a parent immediately sees the card is a
              choice (Sahil: booking choices must look clickable). It's a
              span inside the select button, so a click anywhere — including
              the pill — selects; no nested buttons. */}
          <span
            className="mt-3 flex items-center justify-center gap-1.5 w-full rounded-lg py-2 text-sm font-semibold transition-colors group-hover:brightness-95"
            style={
              isSelected
                ? { backgroundColor: accentColor, color: "#fff" }
                : {
                    border: `1.5px solid ${accentColor}`,
                    color: accentColor,
                    backgroundColor: `${accentColor}0A`,
                  }
            }
          >
            {isSelected ? (
              <>
                <CheckIcon className="h-4 w-4" strokeWidth={3} />
                Selected
              </>
            ) : (
              "Select"
            )}
          </span>
        </button>

        {/* "View profile" — integrated secondary action. Separate tap
            target (stopPropagation) so it never changes selection.
            min-h-[44px] for comfortable tap targets (Apple HIG). Hidden
            for coaches with no meaningful profile content. */}
        {showAbout && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onAbout?.();
            }}
            className="flex items-center gap-1 w-full min-h-[44px] px-4 py-2.5 text-tiny font-semibold border-t transition-colors hover:bg-black/[0.02]"
            style={{ borderColor: "var(--bf-border)", color: accentColor }}
            aria-label={`View ${coach.name || "instructor"}'s profile`}
          >
            View profile
            <ChevronRightIcon className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </StaggeredItem>
  );
}

// ─── Selectable Card wrapper ───

export function SelectableCard({
  isSelected,
  accentColor,
  children,
  onClick,
  className = "",
}: {
  isSelected: boolean;
  accentColor: string;
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`relative w-full text-left rounded-2xl p-4 transition-all duration-200 ${className}`}
      style={
        isSelected
          ? {
              backgroundColor: `${accentColor}04`,
              border: `1.5px solid ${accentColor}`,
              boxShadow: `0 0 0 3px ${accentColor}08`,
            }
          : {
              backgroundColor: "var(--bf-bg-card)",
              border: "1px solid var(--bf-border)",
              boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
            }
      }
    >
      {children}
    </button>
  );
}
